import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { ChangePasswordFormFields } from '../components/ChangePasswordForm/ChangePasswordForm';
import Loading from '../components/Loading/Loading';
import { selectCurrentRole, selectErrorState, selectUser } from '../store/selectors';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import userSlice from '../store/user/slice';
import {
  changeNameRequest,
  PostChangePasswordDTO,
  UpdateMainAliasDTO,
  UpdateUserAvatarDTO,
  UpdateUserDTO,
  UserState,
} from '../store/user/types';
import AvatarForm from '../components/AvatarForm/AvatarForm';
import { Tabs, TabsItem } from '../components/ui';
import history from '../store/history';
import { useTranslation } from 'react-i18next';
import { ValidatedField } from '../store/api.d';
import errorSlice from '../store/error/slice';
import PersonalInfoForm from '../components/PersonalInfoForm/PersonalInfoForm';
import ResearcherInfoForm from '../components/ResearcherInfoForm/ResearcherInfoForm';
import { validationErrorTransformer } from '../store/api';
import UserSettingsForm from '../components/UserSettingsForm/UserSettingsForm';
import Message from '../components/ui/display/Message/Message';
import styled from 'styled-components';
import { Role } from '../store/conference/types';
import SecurityProfileForm from '../components/SecurityProfileForm/SecurityProfileForm';

interface SettingsPageProps {
  userState: UserState;
  role: Role | null;
  validationErrors: ValidatedField[];
  updateUserAction: (data: UpdateUserDTO) => void;
  updateUserAvatarAction: (data: UpdateUserAvatarDTO) => void;
  deleteUserAvatarAction: () => void;
  changeUserPasswordAction: (data: PostChangePasswordDTO) => void;
  updateMainAliasAction: (data: UpdateMainAliasDTO) => void;
  clearValidationErrorsAction: () => void;
  changeNameRequestAction: (data: changeNameRequest) => void;
}

type SectionName = 'personal-info' | 'researcher' | 'settings' | 'security';

const ProfilePage: React.FC<SettingsPageProps> = ({
  userState,
  role,
  validationErrors,
  updateUserAction,
  updateUserAvatarAction,
  deleteUserAvatarAction,
  changeUserPasswordAction,
  updateMainAliasAction,
  clearValidationErrorsAction,
  changeNameRequestAction,
}) => {
  const [section, setSection] = React.useState<SectionName>('personal-info');
  const { t, i18n } = useTranslation();

  useEffect(() => {
    return () => clearValidationErrorsAction();
  }, []);

  if (userState.loading) return <Loading />;

  const user = userState.data;

  let content;
  switch (section) {
    case 'personal-info':
      content = (
        <PersonalInfoForm
          formData={{
            first_name: user.person.first_name,
            middle_name: user.person.middle_name ?? '',
            last_name: user.person.last_name,
            country: user.person.country ?? '',
            organization: user.person.organization ?? '',
            organization2: user.person.organization2 ?? '',
            organization3: user.person.organization3 ?? '',
            organization4: user.person.organization4 ?? '',
            organization5: user.person.organization5 ?? '',
          }}
          onSave={(data) => {
            updateUserAction({
              ...user.person,
              ...data,
            });
          }}
          onCancel={() => history.goBack()}
          onChangeName={user.person.name_validated_at ? changeNameRequestAction : undefined}
        />
      );
      break;
    case 'researcher':
      content = (
        <ResearcherInfoForm
          role={role}
          formData={{
            dblp_key: user.person.dblp_key ?? '',
            google_scholar_id: user.person.google_scholar_id ?? '',
            orcid_id: user.person.orcid_id ?? '',
          }}
          onSave={(data) => {
            updateUserAction({
              ...user.person,
              ...data,
              include_role: !!role,
            });
          }}
          onCancel={() => history.goBack()}
          validation_error={validationErrorTransformer(validationErrors)}
          clearValidationErrorsAction={clearValidationErrorsAction}
        />
      );
      break;
    case 'settings':
      content = (
        <UserSettingsForm
          formData={{
            language: user.person.language,
            timezone: user.person.timezone ?? '',
            date_format: user.person.date_format ?? '',
          }}
          onSave={(data) => {
            updateUserAction({
              ...user.person,
              ...data,
            });
          }}
          onCancel={() => history.goBack()}
        />
      );
      break;
    case 'security':
      content = (
        <SecurityProfileForm
          userId={user.id}
          userAliasById={user.user_alias_by_id}
          passwordSet={user.password_set}
          onUpdateMainAlias={(user_id: number) => updateMainAliasAction({ user_id })}
          onChangePassword={(form: ChangePasswordFormFields) => changeUserPasswordAction(form)}
          validationErrors={validationErrorTransformer(validationErrors)}
          clearValidationErrorsAction={clearValidationErrorsAction}
          onCancel={() => history.goBack()}
        />
      );
      break;
  }

  return (
    <div className="h-full flex flex-col">
      <AvatarForm
        user={user}
        save_handler={(avatar: File) => updateUserAvatarAction({ avatar })}
        delete_handler={() => deleteUserAvatarAction()}
      />
      <p className="text-xl mt-4 mb-7 text-center font-medium">
        {t(`Welcome`)}, {user.person.full_name}
      </p>

      <div className="flex flex-col items-center mobileCtrlProfileSubmenu">
        <Tabs className="flex-grow mb-4 px-40 text-sm gap-x-10">
          <TabsItem
            label={t('Personal info')}
            selected={section == 'personal-info'}
            onClick={() => setSection('personal-info')}
          />
          <TabsItem
            label={t('Researcher')}
            selected={section == 'researcher'}
            onClick={() => setSection('researcher')}
          />
          <TabsItem label={t('Settings')} selected={section == 'settings'} onClick={() => setSection('settings')} />
          <TabsItem label={t('Security')} selected={section == 'security'} onClick={() => setSection('security')} />
        </Tabs>
      </div>
      <div className="flex-1-1-0">
        <div className="w-full h-full flex flex-col">{content}</div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  userState: selectUser(state),
  role: selectCurrentRole(state),
  validationErrors: selectErrorState(state).validationErrors ?? [],
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserAction: (data: UpdateUserDTO) => dispatch(userSlice.actions.UPDATE(data)),
  updateUserAvatarAction: (data: UpdateUserAvatarDTO) => dispatch(userSlice.actions['AVATAR:UPDATE'](data)),
  deleteUserAvatarAction: () => dispatch(userSlice.actions['AVATAR:DELETE']()),
  changeUserPasswordAction: (data: PostChangePasswordDTO) => dispatch(userSlice.actions['CHANGE_PASSWORD'](data)),
  updateMainAliasAction: (data: UpdateMainAliasDTO) => dispatch(userSlice.actions['UPDATE_MAIN_ALIAS'](data)),
  clearValidationErrorsAction: () => dispatch(errorSlice.actions['CLEAR:VALIDATION_ERRORS']()),
  changeNameRequestAction: (data: changeNameRequest) => dispatch(userSlice.actions['CHANGE-NAME-REQUEST'](data)),
});

const StyledMessage = styled(Message)`
  max-width: 40rem;
`;

export default connect(mapStateToProps, mapDispatchToProps)(ProfilePage);
